import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import { Alert } from '@material-ui/lab';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import { serviceRegistry } from '../../../store';
import {
  SELECTOR_DESCRIPTIONS,
  AVAILABILITY_RULE_DESCRIPTIONS,
} from './selectorDescriptions';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  subsection: {
    marginBottom: theme.spacing(2),
  },
  selectorOption: {
    marginBottom: theme.spacing(1),
  },
  optionDescription: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(-0.5),
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
  },
  loadingText: {
    marginLeft: theme.spacing(1),
  },
  formControl: {
    minWidth: 240,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

/**
 * CatalogSelectorSection — Catalog selector + Availability criteria subsections.
 *
 * Payload providers intentionally excluded — rendered by PayloadProviderSection.
 *
 * Props:
 *   catalogSelectorApi  — injected API (default: serviceRegistry.catalogSelectorApi)
 *   capabilities        — { catalogSelectorsV1, availabilityRulesV1 }
 *   onSaved             — called after any successful save (for re-index notice trigger)
 */
export default function CatalogSelectorSection({
  catalogSelectorApi,
  capabilities,
  onSaved,
}) {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const capCatalog = !!capabilities.catalogSelectorsV1;
  const capAvailability = !!capabilities.availabilityRulesV1;

  // ── Catalog selector state ────────────────────────────────────────────────
  const [selectors, setSelectors] = useState([]);
  const [activeSelectorKey, setActiveSelectorKey] = useState('');
  const [excludedIdsInput, setExcludedIdsInput] = useState('');
  const [excludedIdsError, setExcludedIdsError] = useState('');
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [selectorSaving, setSelectorSaving] = useState(false);
  const [selectorError, setSelectorError] = useState(null);
  const [selectorSuccess, setSelectorSuccess] = useState(false);

  // ── Availability criteria state ───────────────────────────────────────────
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [activeRuleKey, setActiveRuleKey] = useState('');
  const [ruleParams, setRuleParams] = useState({});
  const [fallbackWhenAbsent, setFallbackWhenAbsent] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [availabilitySuccess, setAvailabilitySuccess] = useState(false);

  // ── Load catalog selectors ────────────────────────────────────────────────
  const loadSelectors = useCallback(async () => {
    if (!capCatalog || !catalogSelectorApi) return;
    setSelectorLoading(true);
    setSelectorError(null);
    try {
      const [discoverRes, activeRes] = await Promise.all([
        catalogSelectorApi.discoverSelectors(),
        catalogSelectorApi.getActive(),
      ]);
      setSelectors(discoverRes.selectors || []);
      const key = activeRes.selector_key || '';
      setActiveSelectorKey(key);
      const cfg = activeRes.selector_config || {};
      if (key === 'categories_excluded' && Array.isArray(cfg.excluded_category_ids)) {
        setExcludedIdsInput(cfg.excluded_category_ids.join(', '));
      }
    } catch (err) {
      setSelectorError(err.message || t('ui.catalogSelector.catalogSelectorSection.loadSelectorsFailed'));
    } finally {
      setSelectorLoading(false);
    }
  }, [capCatalog, catalogSelectorApi, t]);

  // ── Load availability criteria ────────────────────────────────────────────
  const loadAvailability = useCallback(async () => {
    if (!capAvailability || !catalogSelectorApi) return;
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    try {
      const [discoverRes, activeRes] = await Promise.all([
        catalogSelectorApi.discoverAvailabilityRules(),
        catalogSelectorApi.getActiveAvailabilityCriteria(),
      ]);
      setAvailabilityRules(discoverRes.rules || []);
      const criteria = activeRes.criteria || {};
      setActiveRuleKey(criteria.available_rule_key || '');
      setRuleParams(criteria.rule_params || {});
      setFallbackWhenAbsent(criteria.fallback_when_absent !== false); // default true
    } catch (err) {
      setAvailabilityError(err.message || t('ui.catalogSelector.catalogSelectorSection.loadAvailabilityFailed'));
    } finally {
      setAvailabilityLoading(false);
    }
  }, [capAvailability, catalogSelectorApi, t]);

  useEffect(() => {
    loadSelectors();
    loadAvailability();
  }, [loadSelectors, loadAvailability]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function parseExcludedIds(raw) {
    if (!raw || !raw.trim()) return null;
    const parts = raw.split(',').map((s) => parseInt(s.trim(), 10));
    if (parts.some(Number.isNaN) || parts.some((n) => n <= 0)) return null;
    return parts;
  }

  function resolveSelector(s) {
    const desc = SELECTOR_DESCRIPTIONS[s.selector_key];
    return {
      ...s,
      label: desc?.label ? t(desc.label) : (s.label || s.selector_key),
      description: desc?.description ? t(desc.description) : (s.description || ''),
    };
  }

  function resolveRule(r) {
    const desc = AVAILABILITY_RULE_DESCRIPTIONS[r.available_rule_key];
    return {
      ...r,
      label: desc?.label ? t(desc.label) : (r.label || r.available_rule_key),
      description: desc?.description ? t(desc.description) : (r.description || ''),
    };
  }

  // ── Save handlers ─────────────────────────────────────────────────────────
  const handleSaveSelector = useCallback(async () => {
    setExcludedIdsError('');
    let config = {};
    if (activeSelectorKey === 'categories_excluded') {
      const ids = parseExcludedIds(excludedIdsInput);
      if (!ids) {
        setExcludedIdsError(t('ui.catalogSelector.catalogSelectorSection.invalidCategoryIds'));
        return;
      }
      config = { excluded_category_ids: ids };
    }
    setSelectorSaving(true);
    setSelectorError(null);
    setSelectorSuccess(false);
    try {
      await catalogSelectorApi.setSelector(activeSelectorKey, config);
      setSelectorSuccess(true);
      if (onSaved) onSaved();
    } catch (err) {
      setSelectorError(err.message || t('ui.catalogSelector.catalogSelectorSection.saveSelectorFailed'));
    } finally {
      setSelectorSaving(false);
    }
  }, [catalogSelectorApi, activeSelectorKey, excludedIdsInput, onSaved, t]);

  const handleSaveAvailabilityCriteria = useCallback(async () => {
    setAvailabilitySaving(true);
    setAvailabilityError(null);
    setAvailabilitySuccess(false);
    try {
      await catalogSelectorApi.setAvailabilityCriteria(activeRuleKey, ruleParams, fallbackWhenAbsent);
      setAvailabilitySuccess(true);
      if (onSaved) onSaved();
    } catch (err) {
      setAvailabilityError(err.message || t('ui.catalogSelector.catalogSelectorSection.saveAvailabilityFailed'));
    } finally {
      setAvailabilitySaving(false);
    }
  }, [catalogSelectorApi, activeRuleKey, ruleParams, fallbackWhenAbsent, onSaved, t]);

  if (!capCatalog) return null;

  const showFallbackToggle = capAvailability && activeRuleKey !== 'ps_available_for_order';

  return (
    <Box className={classes.section} data-testid="catalog-selector-section">
      <SettingsCard>
        <Typography variant="h6" gutterBottom><IntlMessages id="ui.catalogSelector.catalogSelectorSection.indexingDataSource" /></Typography>

        {/* Catalog Selector */}
        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom><IntlMessages id="ui.catalogSelector.catalogSelectorSection.catalogSelector" /></Typography>

          {selectorLoading && (
            <Box className={classes.loadingRow}>
              <CircularProgress size={16} data-testid="catalog-selector-loading" />
              <Typography variant="body2" color="textSecondary" className={classes.loadingText}>
                <IntlMessages id="common.loading" />
              </Typography>
            </Box>
          )}

          {selectorError && (
            <Alert severity="error" data-testid="catalog-selector-error">
              {selectorError}
            </Alert>
          )}

          {!selectorLoading && !selectorError && (
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <IntlMessages id="ui.catalogSelector.catalogSelectorSection.selectActiveCatalog" />
              </FormLabel>
              <RadioGroup
                value={activeSelectorKey}
                onChange={(e) => setActiveSelectorKey(e.target.value)}
                data-testid="catalog-selector-radio-group"
              >
              {selectors.map((s) => {
                const resolved = resolveSelector(s);
                return (
                  <Box key={s.selector_key} className={classes.selectorOption}>
                    <FormControlLabel
                      value={s.selector_key}
                      control={<Radio size="small" data-testid={`catalog-selector-radio-${s.selector_key}`} />}
                      label={resolved.label}
                    />
                    {resolved.description && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        className={classes.optionDescription}
                        data-testid={`catalog-selector-desc-${s.selector_key}`}
                      >
                        {resolved.description}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              </RadioGroup>
            </FormControl>
          )}

          {/* Excluded categories input (only for categories_excluded key) */}
          {activeSelectorKey === 'categories_excluded' && (
            <Box mt={1}>
              <TextField
                label={<IntlMessages id="ui.catalogSelector.catalogSelectorSection.excludedCategoryIDsCommaSeparated" />}
                value={excludedIdsInput}
                onChange={(e) => setExcludedIdsInput(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                error={!!excludedIdsError}
                helperText={excludedIdsError || ' '}
                inputProps={{ 'data-testid': 'categories-excluded-ids-input' }}
                FormHelperTextProps={{ 'data-testid': 'categories-excluded-error' }}
              />
            </Box>
          )}

          {selectorSuccess && (
            <Typography variant="body2" color="primary" data-testid="catalog-selector-success">
              <IntlMessages id="ui.catalogSelector.catalogSelectorSection.saved" />
            </Typography>
          )}

          <Box className={classes.actionRow}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveSelector}
              disabled={selectorSaving || selectorLoading}
              startIcon={selectorSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              data-testid="catalog-selector-save-btn"
            >
              {selectorSaving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>

        {/* Availability Criteria */}
        {capAvailability && (
          <>
            <Divider className={classes.divider} />
            <Box>
              <Typography variant="subtitle2" gutterBottom><IntlMessages id="ui.catalogSelector.catalogSelectorSection.availabilityCriteria" /></Typography>

              {availabilityLoading && (
                <Box className={classes.loadingRow}>
                  <CircularProgress size={16} data-testid="availability-criteria-loading" />
                  <Typography variant="body2" color="textSecondary" className={classes.loadingText}>
                    <IntlMessages id="common.loading" />
                  </Typography>
                </Box>
              )}

              {availabilityError && (
                <Alert severity="error" data-testid="availability-criteria-error">
                  {availabilityError}
                </Alert>
              )}

              {!availabilityLoading && !availabilityError && (
                <>
                  <FormControl size="small" variant="outlined" className={classes.formControl}>
                    <Select
                      value={activeRuleKey}
                      onChange={(e) => setActiveRuleKey(e.target.value)}
                      displayEmpty
                      data-testid="availability-criteria-select"
                    >
                      <MenuItem value="" disabled>
                        <em><IntlMessages id="ui.catalogSelector.catalogSelectorSection.selectRule" /></em>
                      </MenuItem>
                      {availabilityRules.map((r) => {
                        const resolved = resolveRule(r);
                        return (
                          <MenuItem
                            key={r.available_rule_key}
                            value={r.available_rule_key}
                            data-testid={`availability-rule-option-${r.available_rule_key}`}
                          >
                            {resolved.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {/* Rule description */}
                  {activeRuleKey && AVAILABILITY_RULE_DESCRIPTIONS[activeRuleKey] && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      className={classes.subsection}
                      data-testid="availability-rule-description"
                    >
                      {t(AVAILABILITY_RULE_DESCRIPTIONS[activeRuleKey].description)}
                    </Typography>
                  )}

                  {/* ps_stock_flag: allow_out_of_stock_orders param */}
                  {activeRuleKey === 'ps_stock_flag' && (
                    <Box mt={1}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            size="small"
                            checked={!!ruleParams.allow_out_of_stock_orders}
                            onChange={(e) => setRuleParams({ allow_out_of_stock_orders: e.target.checked })}
                            data-testid="rule-param-allow-oos-orders"
                          />
                        )}
                        label={<IntlMessages id="ui.catalogSelector.catalogSelectorSection.allowOutOfStockOrdersOrderPolicy" />}
                      />
                    </Box>
                  )}

                  {/* fallback_when_absent toggle (hidden for ps_available_for_order) */}
                  {showFallbackToggle && (
                    <Box mt={1}>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={fallbackWhenAbsent}
                            onChange={(e) => setFallbackWhenAbsent(e.target.checked)}
                            size="small"
                            data-testid="fallback-when-absent-toggle"
                          />
                        )}
                        label={<IntlMessages id="ui.catalogSelector.catalogSelectorSection.includeProductsWithNoStockRecordFallbackWhenStockData" />}
                      />
                    </Box>
                  )}
                </>
              )}

              {availabilitySuccess && (
                <Typography variant="body2" color="primary" data-testid="availability-criteria-success">
                  <IntlMessages id="ui.catalogSelector.catalogSelectorSection.saved" />
                </Typography>
              )}

              <Box className={classes.actionRow}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSaveAvailabilityCriteria}
                  disabled={availabilitySaving || availabilityLoading}
                  startIcon={availabilitySaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  data-testid="availability-criteria-save-btn"
                >
                  {availabilitySaving ? t('common.saving') : t('common.save')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </SettingsCard>
    </Box>
  );
}

CatalogSelectorSection.propTypes = {
  catalogSelectorApi: PropTypes.shape({
    discoverSelectors: PropTypes.func.isRequired,
    getActive: PropTypes.func.isRequired,
    setSelector: PropTypes.func.isRequired,
    discoverAvailabilityRules: PropTypes.func.isRequired,
    getActiveAvailabilityCriteria: PropTypes.func.isRequired,
    setAvailabilityCriteria: PropTypes.func.isRequired,
  }),
  capabilities: PropTypes.shape({
    catalogSelectorsV1: PropTypes.bool,
    availabilityRulesV1: PropTypes.bool,
  }),
  onSaved: PropTypes.func,
};

CatalogSelectorSection.defaultProps = {
  catalogSelectorApi: serviceRegistry.catalogSelectorApi,
  capabilities: {},
  onSaved: null,
};
